<template>
  <PageLoading v-if="loading" />
  <ErrorMessage v-else-if="error" />
  <V1ScreenScaffold
    v-else-if="providers && providers.sections.length > 0"
    :next-disabled="!canContinue"
    data-testid="v1-providers"
    @next="handleContinue"
  >
    <template #subtitle>
      <ProviderAmountSummary
        v-if="amountDisplay"
        :label="labels?.paymentAmount || ''"
        :amount="amountDisplay"
        :next-payment="nextPaymentLine || ''"
        test-id="v1-providers-amount"
        next-payment-test-id="v1-providers-next-payment"
      />
    </template>

    <h1 class="v1-providers__title" data-testid="v1-providers-title">{{ providers.title }}</h1>

    <ProviderCardList :items="cardItems" :selected-key="selectedCardKey" @select="onCardSelect" />
  </V1ScreenScaffold>
  <NoPayments v-else />

  <Modal
    :model-value="activePopup !== null"
    :title="popupTitle"
    :primary-text="popupPrimaryText"
    :secondary-text="popupSecondaryText || undefined"
    placement="bottom"
    data-testid="v1-provider-popup"
    @primary="onPopupPrimary"
    @secondary="onPopupSecondary"
    @update:model-value="onPopupModel"
  >
    <p v-if="activePopup && activePopup.notice">{{ activePopup.notice.body }}</p>
  </Modal>

  <Modal
    :model-value="showCountryNotice"
    :title="countryNoticeTitle"
    :primary-text="countryNoticePrimaryText"
    :secondary-text="countryNoticeSecondaryText || undefined"
    placement="bottom"
    data-testid="v1-country-notice-popup"
    @primary="dismissCountryNotice"
    @secondary="onCountryNoticeSecondary"
    @update:model-value="onCountryNoticeModel"
  >
    <p>{{ countryNoticeBody }}</p>
  </Modal>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Modal } from "@payjoy/design-system";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import ProviderAmountSummary from "@/components/providers-redesign/ProviderAmountSummary.vue";
import ProviderCardList, { ProviderCardItem } from "@/components/providers-redesign/ProviderCardList.vue";
import PageLoading from "@/components/PageLoading.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import NoPayments from "@/components/NoPayments.vue";
import { fetchV1PaymentInfo, fetchV1Providers } from "@/helpers/v1TestHelper";
import {
  IV1Availability,
  IV1ProvidersLabels,
  IV1ProvidersResponse,
  IV1Section,
  IV1SecondaryAction,
  V1AvailabilityState,
  V1SecondaryActionType,
} from "@/types/IV1Providers";
import { IV1Installment, IV1PaymentInfo } from "@/types/IV1PaymentInfo";

interface ActivePopup {
  section: IV1Section;
  index: number;
  state: IV1Availability["state"];
  notice: IV1Availability["notice"];
  secondaryAction: IV1SecondaryAction | null;
}

/**
 * v1 providers screen (PYMT-2287) — renders the pre-resolved section cards from
 * `GET /api/v1/providers/` in the new UI. The endpoint needs a session, so
 * `fetchV1PaymentInfo` runs first; its response also drives the amount block and
 * the server-resolved "next payment" date (bucket-picked by the selected amount,
 * mirroring the legacy `calculateNextValidThrough`). Selecting an `enabled`
 * section enables the Continue action; `blocked`/`confirm` sections open a
 * server-driven notice popup instead.
 */
export default defineComponent({
  name: "V1PaymentProvidersView",
  components: { V1ScreenScaffold, ProviderAmountSummary, ProviderCardList, Modal, PageLoading, ErrorMessage, NoPayments },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const providers = ref<IV1ProvidersResponse | null>(null);
    const labels = computed<IV1ProvidersLabels | undefined>(() => providers.value?.labels);
    const paymentInfo = ref<IV1PaymentInfo | null>(null);
    const loading = ref<boolean>(true);
    const error = ref<boolean>(false);
    const activePopup = ref<ActivePopup | null>(null);
    // Selection is tracked by row index, not section.id: the backend can emit the
    // same `id` for every leaf in a group (id = section_id), so id-based selection
    // would light up every card in that group.
    const selectedIndex = ref<number | null>(null);

    const amountValue = computed<string>(() => (typeof route.query.amount === "string" ? route.query.amount : ""));
    const amountNumber = computed<number>(() => Number(amountValue.value));

    const currencySymbol = computed<string>(() => {
      const sample =
        paymentInfo.value?.installments?.find((i) => i.amount.display)?.amount.display ||
        paymentInfo.value?.minimumPaymentAmount?.display ||
        "";
      return sample.replace(/[\d.,\s]/g, "") || paymentInfo.value?.currency || "";
    });

    // TODO: when the Other-amount redesign lands, forward the server-formatted
    // display so custom amounts don't fall back to this client-side grouping.
    const formatAmount = (raw: string): string => {
      const value = Number(raw);
      if (!Number.isFinite(value)) return `${currencySymbol.value}${raw}`;
      return `${currencySymbol.value}${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value)}`;
    };

    const matchedInstallment = computed<IV1Installment | undefined>(
      () => paymentInfo.value?.installments?.find((i) => Number(i.amount.value) === amountNumber.value),
    );

    const amountDisplay = computed<string>(() => {
      if (!amountValue.value) return "";
      return matchedInstallment.value?.amount.display ?? formatAmount(amountValue.value);
    });

    const nextPaymentDisplay = computed<string | null>(() => {
      if (matchedInstallment.value?.nextPayment?.display) return matchedInstallment.value.nextPayment.display;
      // Bucket-pick: the highest priced installment the amount still covers.
      let picked: IV1Installment | null = null;
      for (const row of paymentInfo.value?.installments ?? []) {
        if (row.days === 0 || !row.nextPayment?.display) continue;
        if (amountNumber.value >= Number(row.amount.value)) {
          if (!picked || Number(row.amount.value) >= Number(picked.amount.value)) picked = row;
        }
      }
      return picked?.nextPayment?.display ?? null;
    });

    const nextPaymentLine = computed<string | null>(() => {
      if (matchedInstallment.value?.nextPayment?.display) return matchedInstallment.value.nextPayment.display;
      let picked: IV1Installment | null = null;
      for (const row of paymentInfo.value?.installments ?? []) {
        if (row.days === 0 || !row.nextPayment?.display) continue;
        if (amountNumber.value >= Number(row.amount.value)) {
          if (!picked || Number(row.amount.value) >= Number(picked.amount.value)) picked = row;
        }
      }
      return picked?.nextPayment?.display ?? null;
    });

    onMounted(async () => {
      try {
        paymentInfo.value = await fetchV1PaymentInfo();
        const amount = amountValue.value || undefined;
        const custom = route.query.custom === "true";
        providers.value = await fetchV1Providers({ amount, custom });
      } catch (fetchError) {
        console.error("[v1] Failed to fetch providers:", fetchError);
        error.value = true;
      } finally {
        loading.value = false;
      }
    });

    const proceed = (section: IV1Section): void => {
      if (section.group) {
        router.push({
          name: "V1ProviderGroupByDevice",
          params: { deviceTag: route.params.deviceTag, groupId: section.id },
          query: route.query,
        });
        return;
      }
      if (!section.provider) return;
      router.push({
        name: "V1PaymentReferenceByDevice",
        params: { deviceTag: route.params.deviceTag },
        query: { ...route.query, providerId: section.provider.name, sectionId: section.provider.id },
      });
    };

    // A card is dimmed (but still tappable → opens its notice popup) when the
    // amount/flags make it unusable — mirrors the native app's greyed-out state.
    const isBlocked = (section: IV1Section): boolean => section.availability.state === V1AvailabilityState.Blocked;

    const handleSelectSection = (section: IV1Section, index: number): void => {
      const { state, notice, secondaryAction } = section.availability;
      if (state === V1AvailabilityState.Enabled) {
        selectedIndex.value = index;
        return;
      }
      if (notice) {
        activePopup.value = { section, index, state, notice, secondaryAction };
      }
    };

    const selectedSection = computed<IV1Section | undefined>(() =>
      selectedIndex.value === null ? undefined : providers.value?.sections[selectedIndex.value],
    );
    // Exposed id of the current selection (derived from the index).
    const selectedId = computed<string | null>(() => selectedSection.value?.id ?? null);

    const cardItems = computed<ProviderCardItem[]>(() =>
      (providers.value?.sections ?? []).map((section, index) => ({
        key: String(index),
        title: section.title,
        subtitle: section.subtitle || undefined,
        logoUrl: section.logoUrl,
        dimmed: isBlocked(section),
        testId: `v1-provider-${section.id}`,
      })),
    );
    const selectedCardKey = computed<string | null>(() =>
      selectedIndex.value === null ? null : String(selectedIndex.value),
    );
    const onCardSelect = (key: string): void => {
      const index = Number(key);
      const section = providers.value?.sections[index];
      if (section) handleSelectSection(section, index);
    };

    const canContinue = computed<boolean>(() => {
      const state = selectedSection.value?.availability.state;
      return state === V1AvailabilityState.Enabled || state === V1AvailabilityState.Confirm;
    });

    const handleContinue = (): void => {
      if (selectedSection.value) proceed(selectedSection.value);
    };

    const closePopup = (): void => {
      activePopup.value = null;
    };

    const onPopupModel = (open: boolean): void => {
      if (!open) closePopup();
    };

    const onPopupPrimary = (): void => {
      const popup = activePopup.value;
      activePopup.value = null;
      if (popup && popup.state === V1AvailabilityState.Confirm) selectedIndex.value = popup.index;
    };

    // Locate the index of a leaf section whose `provider.name` matches the target —
    // mirrors how `secondary_action.provider_name` is emitted server-side.
    const findSectionIndexByProviderName = (name: string): number =>
      providers.value?.sections.findIndex((section) => section.provider?.name === name) ?? -1;

    const onPopupSecondary = (): void => {
      const popup = activePopup.value;
      if (!popup || !popup.secondaryAction) {
        activePopup.value = null;
        return;
      }
      const action = popup.secondaryAction;
      activePopup.value = null;
      if (action.type === V1SecondaryActionType.OpenProvider && action.providerName) {
        const targetIndex = findSectionIndexByProviderName(action.providerName);
        const target = targetIndex >= 0 ? providers.value?.sections[targetIndex] : undefined;
        if (target) handleSelectSection(target, targetIndex);
        return;
      }
      if (action.type === V1SecondaryActionType.RequestExtension) {
        router.push({ name: "ExtensionTypeView", params: { deviceTag: route.params.deviceTag } });
      }
    };

    const countryNoticeDismissed = ref<boolean>(false);
    const showCountryNotice = computed<boolean>(() => !!providers.value?.notice && !countryNoticeDismissed.value);
    const countryNoticeTitle = computed<string>(() => providers.value?.notice?.title || "");
    const countryNoticeBody = computed<string>(() => providers.value?.notice?.body || "");
    const countryNoticePrimaryText = computed<string>(() => t("close"));
    const hasCountryExtension = computed<boolean>(
      () => providers.value?.notice?.secondaryAction?.type === V1SecondaryActionType.RequestExtension,
    );
    const countryNoticeSecondaryText = computed<string>(() =>
      hasCountryExtension.value ? providers.value?.notice?.secondaryAction?.label ?? "" : "",
    );

    const dismissCountryNotice = (): void => {
      countryNoticeDismissed.value = true;
    };

    const onCountryNoticeModel = (open: boolean): void => {
      if (!open) dismissCountryNotice();
    };

    const onCountryNoticeSecondary = (): void => {
      dismissCountryNotice();
      router.push({ name: "ExtensionTypeView", params: { deviceTag: route.params.deviceTag } });
    };

    const popupTitle = computed<string>(() => activePopup.value?.notice?.title || "");
    const popupPrimaryText = computed<string>(() => {
      const popup = activePopup.value;
      if (popup?.state === V1AvailabilityState.Confirm) return t("continue");
      if (popup?.secondaryAction?.type === V1SecondaryActionType.RequestExtension) return t("use-another-method");
      return t("close");
    });
    const popupSecondaryText = computed<string>(() => {
      const popup = activePopup.value;
      if (!popup) return "";
      if (popup.secondaryAction) return popup.secondaryAction.label;
      return popup.state === V1AvailabilityState.Confirm ? t("cancel") : "";
    });

    return {
      labels,
      providers,
      loading,
      error,
      activePopup,
      selectedIndex,
      selectedId,
      isBlocked,
      cardItems,
      selectedCardKey,
      onCardSelect,
      amountDisplay,
      nextPaymentDisplay,
      nextPaymentLine,
      canContinue,
      handleSelectSection,
      handleContinue,
      onPopupModel,
      onPopupPrimary,
      onPopupSecondary,
      popupTitle,
      popupPrimaryText,
      popupSecondaryText,
      showCountryNotice,
      countryNoticeTitle,
      countryNoticeBody,
      countryNoticePrimaryText,
      countryNoticeSecondaryText,
      dismissCountryNotice,
      onCountryNoticeModel,
      onCountryNoticeSecondary,
    };
  },
});
</script>

<style scoped>
.v1-providers__title {
  margin: 32px 0 24px 0;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

@media (min-width: 768px) {
  .v1-providers__title {
    text-align: center;
  }
}
</style>
